import type { EntityFieldDef, WorkplaceFieldOption } from './types'

export type OptionEntry = { value: string; label: string }

/** Minimum similarity (0–1) to auto-map a source enum to a CRM option. */
export const VALUE_MAP_SUGGEST_THRESHOLD = 0.85

export function normalizeOptionKey(str: string): string {
  return str
    .toLowerCase()
    .replace(/[_\-\s]+/g, '')
    .replace(/[^a-z0-9]/g, '')
}

function levenshteinDistance(a: string, b: string): number {
  const matrix: number[][] = []

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i]
  }

  for (let j = 0; j <= a.length; j++) {
    matrix[0]![j] = j
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i]![j] = matrix[i - 1]![j - 1]!
      } else {
        matrix[i]![j] = Math.min(
          matrix[i - 1]![j - 1]! + 1,
          matrix[i]![j - 1]! + 1,
          matrix[i - 1]![j]! + 1,
        )
      }
    }
  }

  return matrix[b.length]![a.length]!
}

export function optionStringSimilarity(a: string, b: string): number {
  const normalizedA = normalizeOptionKey(a)
  const normalizedB = normalizeOptionKey(b)

  if (normalizedA === normalizedB) return 1

  const maxLength = Math.max(normalizedA.length, normalizedB.length)
  if (maxLength === 0) return 1

  const distance = levenshteinDistance(normalizedA, normalizedB)
  return 1 - distance / maxLength
}

/** Guess the best CRM option for a source enum value, or null if no obvious match. */
export function suggestCrmOptionValue(
  source: OptionEntry,
  crmOptions: OptionEntry[],
  threshold = VALUE_MAP_SUGGEST_THRESHOLD,
): string | null {
  if (crmOptions.length === 0) return null

  const exactValue = crmOptions.find((opt) => opt.value === source.value)
  if (exactValue) return exactValue.value

  const normSourceValue = normalizeOptionKey(source.value)
  const normSourceLabel = normalizeOptionKey(source.label)

  for (const opt of crmOptions) {
    const normValue = normalizeOptionKey(opt.value)
    const normLabel = normalizeOptionKey(opt.label)
    if (normValue === normSourceValue || normValue === normSourceLabel) {
      return opt.value
    }
    if (normLabel === normSourceValue || normLabel === normSourceLabel) {
      return opt.value
    }
  }

  let best: { value: string; similarity: number } | null = null

  for (const opt of crmOptions) {
    const candidates = [opt.value, opt.label]
    const needles = [source.value, source.label]

    for (const needle of needles) {
      for (const candidate of candidates) {
        const similarity = optionStringSimilarity(needle, candidate)
        if (
          similarity >= threshold &&
          (!best || similarity > best.similarity)
        ) {
          best = { value: opt.value, similarity }
        }
      }
    }
  }

  return best?.value ?? null
}

/** Build a value map with fuzzy guesses for unmapped source options. */
export function suggestValueMap(
  sourceOptions: OptionEntry[],
  crmOptions: OptionEntry[],
  existing?: Record<string, string>,
): Record<string, string> | undefined {
  const result: Record<string, string> = { ...(existing ?? {}) }
  let changed = false

  for (const source of sourceOptions) {
    if (Object.prototype.hasOwnProperty.call(result, source.value)) continue
    const suggested = suggestCrmOptionValue(source, crmOptions)
    if (suggested) {
      result[source.value] = suggested
      changed = true
    }
  }

  if (!changed) return existing
  return Object.keys(result).length > 0 ? result : undefined
}

export function normalizeEntityFieldOptionsList(
  options?: EntityFieldDef['options'],
): Array<{ value: string; label: string }> {
  if (!options?.length) return []
  return options.map((opt) =>
    typeof opt === 'string' ? { value: opt, label: opt } : opt,
  )
}

export function mapWorkplaceFieldFromApi(field: {
  id: string
  handle: string
  name: string
  label?: string
  type?: string
  isUnique?: boolean
  definition?: {
    hasOptions?: boolean
    options?: Array<{ value: string; label: string }>
  } | null
}): WorkplaceFieldOption {
  const options = (field.definition?.options ?? []).map((opt) => ({
    value: opt.value,
    label: opt.label,
  }))

  return {
    id: field.id,
    handle: field.handle,
    name: field.name || field.label || field.handle,
    type: field.type,
    isUnique: Boolean(field.isUnique),
    hasOptions: Boolean(field.definition?.hasOptions || options.length),
    options,
  }
}

/** Resolve CRM option list for enum value mapping UI. */
export function resolveCrmFieldOptionsForMapping(
  targetField: WorkplaceFieldOption | undefined,
  sourceOptions: Array<{ value: string; label: string }>,
): Array<{ value: string; label: string }> {
  if (targetField?.options?.length) return targetField.options
  if (sourceOptions.length === 0 || !targetField) return []

  // Target field is option-backed but options were not loaded yet (common right
  // after auto-provision). Fall back to source enum values for 1:1 mapping UX.
  if (targetField.hasOptions) return sourceOptions

  const type = targetField.type?.toUpperCase()
  if (type === 'STRING' || type === 'LONG_STRING') return sourceOptions

  return []
}
